import { Box, Typography, useTheme, Button, Grid, Backdrop, CircularProgress, Divider, IconButton } from "@mui/material"
import { LoadingButton } from "@mui/lab";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "../../../utils/dayjs";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from 'yup';
import MFSelectField from "../../../lib/formik/SelectField";
import { BulkDownloadInvoiceResponseBody, BulkFreezeInvoiceResponseBody, BulkShareInvoiceResponseBody, BulkUnfreezeInvoiceResponseBody, FamilyObj, FreezeInvoiceResponseBody, GenerateRiaFeeResponseBody, GetFamilyInvoiceInfoResponseBody, GetInvoiceControlListResponseBody, GetReportFamiliesListResponseBody, GetReportRmListResponseBody, InvoiceControlListObj, ReportRmObj, RiaFeeReportStatusObj, UnfreezeInvoiceResponseBody, UpdateRiaFeeReportStatusResponseBody } from "../../../redux-store/types/onBoarding";
import { useDispatch, useSelector } from "react-redux";
import { bulkDownloadInvoice, bulkFreezeInvoice, bulkShareInvoice, bulkUnfreezeInvoice, downloadRiaFeeInvoice, freezeInvoice, generateRiaFee, getFamilyInvoiceInfo, getInvoiceControlList, getReportFamiliesList, getReportRmList, unfreezeInvoice, updateRiaFeeReportStatus } from "../../../redux-store/actions/reports";
import { showError } from "../../../redux-store/actions/auth";
import FormikSearchDropdown from "../../../lib/formik/FormikSearchDropdown";
import { InfoIconOutlined, NoFilterSelectedIcon, RefreshIcon } from "../../../assets/SvgIcons";
import ReportGenerationTable from "./ReportGenerationTable";
import { Order, PaginationObj, SortingObj } from "../../investorOverview/types";
import { RootStateType } from "../../../redux-store/reducers";
import { useSnackbar } from "notistack";
import ClientStmtTable from "./ClientStmtTable";
import { CONTENT_TYPE } from "../../../utils/constant";
import { reportDownload } from "../../Reports/report-utils";
import NoData from "../NoData";


interface ModifiedFamilyObj extends FamilyObj {
    label: string;
    value: string;
}

interface ModifiedReportRmObj extends ReportRmObj {
    label: string;
    value: string;
}

interface Values {
    year: string;
    quarter: string;
    rm: ModifiedReportRmObj | null;
    family: ModifiedFamilyObj | null;
    status: string;
}

const initialValues: Values = {
    year: "",
    quarter: "",
    rm: null,
    family: null,
    status: ""
}

const startYear = 2025
const currentYear = dayjs().year()

const YEARS: Array<{ key: string; value: string }> = []

for (let year = startYear; year <= currentYear; year++) {
    YEARS.push({
        key: `${year} - ${year + 1}`,
        value: `${year}`
    })
}

const QUARTERS = [
    { key: "April-June", value: "Q1" },
    { key: "July-September", value: "Q2" },
    { key: "October-December", value: "Q3" },
    { key: "January-March", value: "Q4" }
]

const STATUS = [
    { key: "All", value: "all" },
    { key: "Freezed", value: "FREEZED" },
    { key: "Unfreezed", value: "UNFREEZED" },
    // NOT_GENERATED and NON_BILLABLE were added for the bulk-action feature so
    // users can filter to invoices not yet generated or that aren't billable.
    // Bulk actions are intentionally restricted for these statuses (see the
    // isBulkActionDisabledBase / button disabled logic below).
    { key: "NotGenerated", value: "NOT_GENERATED" },
    { key: "NonBillable", value: "NON_BILLABLE" }
]

const validationSchema = Yup.object().shape({
    year: Yup
        .string()
        .required('Please select Year'),
    quarter: Yup
        .string()
        .required('Please select Quarter'),
    rm: Yup
        .object()
        .nullable()
        .test(
            "rm-required",
            "Please select RM",
            function (value) { return !!value }
        ),
    family: Yup
        .object()
        .nullable()
        .test(
            "family-required",
            "Please select Family",
            function (value) { return !!value }
        ),
    status: Yup
        .string()
        .required('Please select Status')
})

export default function ReportGeneration(): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const { enqueueSnackbar } = useSnackbar();

    const formikRef = useRef<FormikProps<Values>>(null);

    // Data from Redux
    const { iwappRmId } = useSelector((state: RootStateType) => state.id);

    // State
    const [rmList, setRmList] = useState<Array<ModifiedReportRmObj>>([])
    const [familiesList, setFamiliesList] = useState<Array<ModifiedFamilyObj>>([])

    const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
    // The status that was actually applied (snapshotted on "Apply Filters"), kept
    // separate from the live form value so bulk-action availability reflects the
    // table currently shown, not an unapplied dropdown change.
    const [appliedStatus, setAppliedStatus] = useState<string>("");

    const [isReportGenerationTableDataLoading, setIsReportGenerationTableDataLoading] = useState<boolean>(false)
    const [reportGenerationTableData, setReportGenerationTableData] = useState<Array<InvoiceControlListObj>>([])
    const [reportGenerationTablePaginationObj, setReportGenerationTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    // Row selection state for bulk actions. Rows are keyed by billing_entity_id,
    // so selection survives pagination (keys reference the full dataset, not just
    // the current page's slice).
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<string>>([])
    const [isBulkFreezeLoading, setBulkFreezeLoading] = useState<boolean>(false)
    const [isBulkUnfreezeLoading, setBulkUnfreezeLoading] = useState<boolean>(false)
    const [isBulkShareInvoiceLoading, setBulkShareInvoiceLoading] = useState<boolean>(false)

    const [isSelectingOptionLoading, setSelectingOptionLoading] = useState<boolean>(false)
    const [isFilterAppliedLoading, setFilterAppliedLoading] = useState<boolean>(false)

    const [isClientStmtsTableDataLoading, setClientStmtsTableDataLoading] = useState<boolean>(false)
    const [clientStmtsTableData, setClientStmtsTableData] = useState<Array<RiaFeeReportStatusObj>>([])
    const [clientStmtsTablePaginationObj, setClientStmtsTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [clientStmtsTableSortingObj, setClientStmtsTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: "initiatedAt"
    })

    const [isBulkDownloadLoading, setBulkDownloadLoading] = useState<boolean>(false)
    const [isBulkDownloadInvoiceLoading, setBulkDownloadInvoiceLoading] = useState<boolean>(false)

    const fetchClientStmtsTableData = async ({
        currentPage,
        rowsPerPage,
        sort,
        order,
    }: {
        currentPage: number;
        rowsPerPage: number;
        sort: string;
        order: Order;
    }) => {
        try {
            setClientStmtsTableDataLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                sort,
                order: `${order ?? ""}`.toUpperCase(),
                rowsPerPage,
                currentPage
            }

            const {
                count = 0,
                reports = []
            } = await (dispatch(updateRiaFeeReportStatus(payload)) as unknown as Promise<UpdateRiaFeeReportStatusResponseBody>)

            setClientStmtsTableData(reports)
            setClientStmtsTableSortingObj({
                order,
                orderBy: sort
            })
            setClientStmtsTablePaginationObj({
                currentPage: currentPage - 1,
                rowsPerPage,
                totalRows: count
            })
        } catch (error) {
            console.log(error)
        } finally {
            setClientStmtsTableDataLoading(false)
        }
    }

    useEffect(() => {
        fetchClientStmtsTableData({
            currentPage: clientStmtsTablePaginationObj.currentPage + 1,
            rowsPerPage: clientStmtsTablePaginationObj.rowsPerPage,
            sort: clientStmtsTableSortingObj.orderBy,
            order: clientStmtsTableSortingObj.order,
        })
    }, [])

    const handleRefresh = async () => {
        await fetchClientStmtsTableData({
            currentPage: 1,
            rowsPerPage: clientStmtsTablePaginationObj.rowsPerPage,
            sort: clientStmtsTableSortingObj.orderBy,
            order: clientStmtsTableSortingObj.order,
        })
    }

    const handleClientStmtsTableSort = async (orderBy: string, order: Order) => {
        await fetchClientStmtsTableData({
            currentPage: 1,
            rowsPerPage: clientStmtsTablePaginationObj.rowsPerPage,
            sort: orderBy,
            order,
        })
    }

    const handleClientStmtsTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        await fetchClientStmtsTableData({
            currentPage: 1,
            rowsPerPage: Number(event.target.value),
            sort: clientStmtsTableSortingObj.orderBy,
            order: clientStmtsTableSortingObj.order,
        })
    }

    const handleClientStmtsTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        await fetchClientStmtsTableData({
            currentPage: page + 1,
            rowsPerPage: clientStmtsTablePaginationObj.rowsPerPage,
            sort: clientStmtsTableSortingObj.orderBy,
            order: clientStmtsTableSortingObj.order,
        })
    }

    const handleResetFilters = () => {
        if (!formikRef?.current) {
            return
        }

        const { resetForm } = formikRef.current

        resetForm()
        setHasAppliedFilters(false)
        setAppliedStatus("")
    };

    const fetchReportGenerationTableData = async ({
        year,
        quarter,
        rm,
        family,
        status
    }: Values) => {
        try {
            setIsReportGenerationTableDataLoading(true)
            setSelectedRowKeys([])

            const payload = {
                familyIds: family?.family_id,
                year: Number(year),
                quarter,
                rmIds: rm?.iwappRmId,
                invoiceState: `${status ?? ""}`.toLowerCase() !== 'all' ? status : null
            }

            const response = await (dispatch(getInvoiceControlList(payload)) as unknown as Promise<GetInvoiceControlListResponseBody>);

            if (!response) {
                setReportGenerationTableData([])
                setReportGenerationTablePaginationObj({
                    currentPage: 0,
                    rowsPerPage: 5,
                    totalRows: 0
                });
            }

            if (!response?.isSuccess) {
                dispatch(showError(response?.message || "Data not found"))
                setReportGenerationTableData([])
                setReportGenerationTablePaginationObj({
                    currentPage: 0,
                    rowsPerPage: 5,
                    totalRows: 0
                });

                return
            }

            if (response.isSuccess) {
                setReportGenerationTableData(response?.data ?? [])
                setReportGenerationTablePaginationObj(prevState => ({
                    ...prevState,
                    totalRows: response?.rows ?? 0
                }))
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsReportGenerationTableDataLoading(false)
        }
    }

    const handleApplyFilters = async (values: Values) => {
        try {
            setHasAppliedFilters(true)
            setAppliedStatus(values.status)
            setFilterAppliedLoading(true)

            await fetchReportGenerationTableData({
                year: values.year,
                quarter: values.quarter,
                rm: values.rm,
                family: values.family,
                status: values.status
            })
        } catch (error) {
            console.log(error)
        } finally {
            setFilterAppliedLoading(false)
        }
    };

    const fetchFamiliesData = async (searchInput: string) => {
        try {
            if (!formikRef?.current) {
                return
            }

            const { values } = formikRef.current

            // if (values.family) {
            //     return;
            // }

            if (`${searchInput ?? ""}`.trim().length !== 0) {

                const payload = {
                    rmId: values?.rm?.iwappRmId,
                    searchInput: `${searchInput ?? ""}`.trim()
                }
                const response = await (dispatch(getReportFamiliesList(payload)) as unknown as Promise<GetReportFamiliesListResponseBody>);

                if (!response?.isSuccess) {
                    dispatch(showError(response?.message || "Data not found"))
                    setFamiliesList([])

                    return
                }

                if (response?.isSuccess) {
                    const modifiedFamiliesList = (response?.families ?? []).map((obj: FamilyObj) => ({
                        ...obj,
                        label: `${obj?.family_name ?? ""} ${obj?.family_id ? `(${obj.family_id})` : ''}`,
                        value: obj?.family_id
                    }))

                    setFamiliesList(modifiedFamiliesList)
                }
            } else {
                setFamiliesList([])
            }
        } catch (error) {
            console.log(error)
        }
    }

    const fetchRmListData = async (searchInput: string) => {
        try {
            if (!formikRef?.current) {
                return
            }

            // const { values } = formikRef.current

            // if (values.rm) {
            //     return;
            // }

            if (`${searchInput ?? ""}`.trim().length !== 0) {

                const payload = {
                    searchInput: `${searchInput ?? ""}`.trim()
                }
                const response = await (dispatch(getReportRmList(payload)) as unknown as Promise<GetReportRmListResponseBody>);

                if (!response) {
                    setRmList([])

                    return
                }

                if (response) {

                    const modifiedRmList = (response?.rms ?? []).map((obj: ReportRmObj) => ({
                        ...obj,
                        label: `${obj?.name ?? ""} ${obj?.iwappRmId ? `(${obj.iwappRmId})` : ""}`,
                        value: obj?.iwappRmId
                    }))

                    setRmList(modifiedRmList)
                }
            } else {
                setRmList([])
            }
        } catch (error) {
            console.log(error)
        }
    }

    const handleRmChange = async (selectedObj: Record<string, any>) => {
        if (!formikRef?.current) {
            return
        }

        const { values, setValues } = formikRef.current

        setValues({
            ...values,
            rm: selectedObj as ModifiedReportRmObj,
            family: null,
        })

        setFamiliesList([])
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);

        setReportGenerationTablePaginationObj(prev => ({
            ...prev,
            currentPage: 0,
            rowsPerPage: newRowsPerPage
        }));
    }

    const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setReportGenerationTablePaginationObj(prev => ({
            ...prev,
            currentPage: page
        }));
    }

    const handleUnfreezeInvoice = async (currentRow: InvoiceControlListObj) => {
        try {
            if (!formikRef?.current) {
                return
            }

            setSelectingOptionLoading(true)

            const { values } = formikRef.current

            const payload = {
                familyId: currentRow?.family_id,
                year: Number(values?.year),
                quarter: values?.quarter,
                billingEntityId: currentRow?.billing_entity_id,
                rmId: `${iwappRmId}`,
            }

            const response = await (dispatch(unfreezeInvoice(payload)) as unknown as Promise<UnfreezeInvoiceResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });

                return true
            }
            if (!response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });

                return false
            }
        } catch (error) {
            console.log(error)
        } finally {
            setSelectingOptionLoading(false)
        }
    }

    const handleFreezeInvoice = async (currentRow: InvoiceControlListObj) => {
        try {
            if (!formikRef?.current) {
                return
            }

            setSelectingOptionLoading(true)

            const { values } = formikRef.current

            const payload = {
                familyId: currentRow?.family_id,
                year: Number(values?.year),
                quarter: values?.quarter,
                billingEntityId: currentRow?.billing_entity_id,
                rmId: `${iwappRmId}`,
            }

            const response = await (dispatch(freezeInvoice(payload)) as unknown as Promise<FreezeInvoiceResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });

                return true
            }
            if (!response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });

                return false
            }
        } catch (error) {
            console.log(error)
        } finally {
            setSelectingOptionLoading(false)
        }
    }

    const handleGenerateReport = async ({
        familyIds,
        year,
        quarter,
        billingEntityIds,
        rmIds,
        // familyName,
        // billingEntityName,
        investorType,
        familyHead,
        loggedinUser,
        invoiceState,
        // rmName
    }: {
        familyIds: string[] | null;
        year: number;
        quarter: string;
        billingEntityIds: string[] | null;
        rmIds: string[] | null;
        // familyName: string;
        // billingEntityName: string | null;
        investorType: string;
        familyHead: string[] | string;
        loggedinUser: string;
        invoiceState: string | null;
        // rmName: string;
    }) => {
        const payload = {
            familyIds,
            year,
            quarter,
            billingEntityIds,
            rmIds,
            // familyName,
            // billingEntityName,
            investorType,
            familyHead,
            loggedinUser,
            invoiceState,
            // rmName
        }

        const response = await (dispatch(generateRiaFee(payload)) as unknown as Promise<GenerateRiaFeeResponseBody>)

        if (response?.isSuccess) {
            enqueueSnackbar(`${response?.message ?? ""}`, {
                variant: 'success',
                autoHideDuration: 5000,
            });

            await handleRefresh()

            return
        }
        if (!response?.isSuccess) {
            enqueueSnackbar(`${response?.message ?? ""}`, {
                variant: 'error',
                autoHideDuration: 5000,
            });

            return
        }
    }

    const handleRowDownloadReport = async (currentRow: InvoiceControlListObj) => {
        try {
            if (!formikRef?.current) {
                return
            }

            setSelectingOptionLoading(true)

            const { values } = formikRef.current

            const familyIds = [currentRow?.family_id].filter(Boolean).map((id) => `${id}`)
            const billingEntityIds = [currentRow?.billing_entity_id].filter(Boolean).map((id) => `${id}`)
            const rmIds = [currentRow?.rm_id].filter(Boolean).map((id) => `${id}`)

            const payload = {
                familyIds: familyIds.length ? familyIds : null,
                year: Number(values?.year),
                quarter: values?.quarter,
                billingEntityIds: billingEntityIds.length ? billingEntityIds : null,
                rmIds: rmIds.length ? rmIds : null,
                //  familyName: currentRow?.family_name,
                // billingEntityName: currentRow?.billing_entity_name,
                investorType: `${currentRow?.type ?? ""}`.toLowerCase(),
                familyHead: currentRow?.family_head,
                loggedinUser: `${iwappRmId}`,
                // invoiceState: "all" status filter -> null, otherwise this entity's
                // own invoice_state (falling back to the applied status filter).
                invoiceState: `${values?.status ?? ""}`.toLowerCase() !== 'all' ? (currentRow?.invoice_state ?? values?.status) : null,
                // rmName: currentRow?.rm_name
            }

            await handleGenerateReport(payload)

        } catch (error) {
            console.log(error)
        } finally {
            setSelectingOptionLoading(false)
        }
    }

    const handleReportGenerationTableMenuItemClick = async (selectedMenuItem: string, currentRow: InvoiceControlListObj) => {
        if (!formikRef?.current) {
            return
        }

        const { values } = formikRef.current

        switch (`${selectedMenuItem}`.toLowerCase()) {
            case "unfreeze": {
                const response = await handleUnfreezeInvoice(currentRow)

                if (response) {
                    await fetchReportGenerationTableData({
                        year: values.year,
                        quarter: values.quarter,
                        rm: values.rm,
                        family: values.family,
                        status: values.status
                    })

                    await handleRefresh()
                }

                break;
            }
            case "freeze": {
                const response = await handleFreezeInvoice(currentRow)

                if (response) {
                    await fetchReportGenerationTableData({
                        year: values.year,
                        quarter: values.quarter,
                        rm: values.rm,
                        family: values.family,
                        status: values.status
                    })

                    await handleRefresh()
                }

                break;
            }
            // Menu case must stay in sync with the label emitted by
            // ReportGenerationTable's ACTION_MENU_MAP ("Generate Report"), which is
            // lower-cased before this switch. handleRowDownloadReport builds the
            // array-shaped generate payload for the single clicked row.
            case "generate report": {
                await handleRowDownloadReport(currentRow)

                break;
            }
        }
    }

    const handleShareInvoice = async (currentRow: RiaFeeReportStatusObj) => {
        try {
            setSelectingOptionLoading(true)

            const payload = {
                familyId: currentRow?.familyId,
                year: Number(currentRow?.year),
                quarter: currentRow?.quarter,
                billingEntityId: currentRow?.billingEntityId,
                investorEmail: currentRow?.investorEmail,
                rmEmail: currentRow?.rmEmail,
                id: Number(currentRow?.id)
            }

            const response = await (dispatch(getFamilyInvoiceInfo(payload)) as unknown as Promise<GetFamilyInvoiceInfoResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });

                return
            }
            if (!response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });

                return
            }
        } catch (error) {
            console.log(error)
        } finally {
            setSelectingOptionLoading(false)
        }
    }

    const handleDownloadInvoice = async (currentRow: RiaFeeReportStatusObj) => {
        try {
            setSelectingOptionLoading(true)

            const payload = {
                familyId: currentRow?.familyId,
                year: Number(currentRow?.year),
                quarter: currentRow?.quarter,
                billingEntityId: currentRow?.billingEntityId,
                investorEmail: currentRow?.investorEmail,
                rmEmail: currentRow?.rmEmail,
                id: Number(currentRow?.id)
            }

            const response = await (dispatch(downloadRiaFeeInvoice(payload)) as unknown as Promise<any>)

            if (response && [CONTENT_TYPE.PDF].includes(response.type)) {
                enqueueSnackbar('File downloaded successfully.', {
                    variant: 'success',
                    autoHideDuration: 2000,
                });

                reportDownload(response as unknown as Blob, `Invoice-${currentRow?.billingEntityId}.pdf`);
            } else {
                enqueueSnackbar('File download failed!.', {
                    variant: 'error',
                    autoHideDuration: 2000,
                });
            }

        } catch (error) {
            console.log(error)
        } finally {
            setSelectingOptionLoading(false)
        }
    }

    const handleClientStmtTableMenuItemClick = async (selectedMenuItem: string, currentRow: RiaFeeReportStatusObj) => {
        switch (`${selectedMenuItem}`.toLowerCase()) {
            case "download report": {
                if (currentRow?.reportUrl) {
                    window.open(currentRow.reportUrl, "_blank")
                }
                break;
            }
            case "share invoice": {
                await handleShareInvoice(currentRow)
                break;
            }
            case "download invoice": {
                await handleDownloadInvoice(currentRow)
                break;
            }
        }
    }


    const currentTableData = useMemo<Array<InvoiceControlListObj>>(() => {
        const { currentPage, rowsPerPage } = reportGenerationTablePaginationObj;

        const startIndex = currentPage * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;

        // Slice to the current page and stamp each row with its `checked` flag
        // derived from the selected keys, so the table checkboxes stay in sync.
        return reportGenerationTableData.slice(startIndex, endIndex).map(row => ({
            ...row,
            checked: selectedRowKeys.includes(`${row?.billing_entity_id ?? ""}`)
        }));
    }, [
        reportGenerationTableData,
        reportGenerationTablePaginationObj.currentPage,
        reportGenerationTablePaginationObj.rowsPerPage,
        selectedRowKeys
    ]);

    // Full selected rows across all pages (not just the current slice), used as
    // the source of billing_entity_ids for every bulk action payload.
    const selectedRows = useMemo<Array<InvoiceControlListObj>>(() => {
        return reportGenerationTableData.filter(row => selectedRowKeys.includes(`${row?.billing_entity_id ?? ""}`));
    }, [reportGenerationTableData, selectedRowKeys]);

    // "Select all" in the header selects every record across all pages,
    // not just the rows visible on the current page.
    const isAllRecordsSelected = reportGenerationTableData.length > 0 &&
        reportGenerationTableData.every(row => selectedRowKeys.includes(`${row?.billing_entity_id ?? ""}`));

    const isSomeRecordsSelected = reportGenerationTableData.some(row => selectedRowKeys.includes(`${row?.billing_entity_id ?? ""}`));

    // Applied-status flags drive which bulk-action buttons are enabled.
    const isStatusAll = `${appliedStatus}`.toLowerCase() === "all"
    const isStatusFreezed = `${appliedStatus}`.toLowerCase() === "freezed"
    const isStatusUnfreezed = `${appliedStatus}`.toLowerCase() === "unfreezed"
    const isStatusNotGenerated = `${appliedStatus}`.toLowerCase() === "not_generated"
    const isStatusNonBillable = `${appliedStatus}`.toLowerCase() === "non_billable"

    // Bulk actions are only available for a specific status (Freezed/Unfreezed),
    // never when the "All" status is applied.
    const isAnyBulkActionLoading = isBulkFreezeLoading || isBulkUnfreezeLoading || isBulkDownloadLoading || isBulkDownloadInvoiceLoading || isBulkShareInvoiceLoading
    const isBulkActionDisabledBase = isReportGenerationTableDataLoading || isAnyBulkActionLoading || !selectedRows.length || isStatusAll || isStatusNonBillable
    // Bulk "Generate Report" is also allowed under the "All" status (unlike the
    // other bulk actions), so it uses its own disabled flag that omits isStatusAll.
    const isBulkGenerateReportDisabled = isReportGenerationTableDataLoading || isAnyBulkActionLoading || !selectedRows.length || isStatusNonBillable

    // Toggle a single row's selection by its billing_entity_id key.
    const handleReportGenerationTableCheckboxClick = (currentRow: InvoiceControlListObj, checked: boolean) => {
        const key = `${currentRow?.billing_entity_id ?? ""}`

        setSelectedRowKeys(prevState => checked
            ? [...prevState, key]
            : prevState.filter(rowKey => rowKey !== key))
    }

    // Select or clear every record across all pages (keys from the full dataset).
    const handleReportGenerationTableSelectAllClick = (checked: boolean) => {
        setSelectedRowKeys(checked
            ? reportGenerationTableData.map(row => `${row?.billing_entity_id ?? ""}`)
            : [])
    }

    // Bulk freeze: freezes only the selected rows that are currently unfreezed.
    // If none of the selected rows qualify, it warns and makes no API call.
    const handleBulkFreezeReport = async () => {
        try {
            if (!formikRef?.current) {
                return
            }

            setBulkFreezeLoading(true)

            const rowsToFreeze = selectedRows.filter(
                row => `${row?.invoice_state ?? ""}`.toLowerCase() === "unfreeze" ||
                    `${row?.invoice_state ?? ""}`.toLowerCase() === "unfreezed"
            )

            if (!rowsToFreeze.length) {
                enqueueSnackbar("No unfreezed records selected to freeze.", {
                    variant: 'warning',
                    autoHideDuration: 5000,
                });

                return
            }

            const { values } = formikRef.current

            // familyIds comes from the Family filter: a specific family -> [family_id],
            // "all families" (no family selected) -> null.
            const familyIds = values?.family?.family_id
                ? [values.family.family_id]
                : null

            const billingEntityIds = rowsToFreeze
                .map(row => row?.billing_entity_id)
                .filter(Boolean)

            const payload = {
                year: Number(values?.year),
                quarter: values?.quarter,
                familyIds,
                loggedinUser: `${iwappRmId}`,
                rmIds: values?.rm?.iwappRmId ? [values.rm.iwappRmId] : null,
                invoiceState: `${values?.status ?? ""}`.toLowerCase() !== 'all' ? values?.status : null,
                billingEntityIds: billingEntityIds.length ? billingEntityIds : null
            }

            const response = await (dispatch(bulkFreezeInvoice(payload)) as unknown as Promise<BulkFreezeInvoiceResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });

                await fetchReportGenerationTableData({
                    year: values.year,
                    quarter: values.quarter,
                    rm: values.rm,
                    family: values.family,
                    status: values.status
                })

                await handleRefresh()
            } else {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            }
        } catch (error) {
            console.log(error)
        } finally {
            setBulkFreezeLoading(false)
        }
    }

    // Bulk unfreeze: unfreezes only the selected rows that are currently freezed.
    // If none of the selected rows qualify, it warns and makes no API call.
    const handleBulkUnfreezeReport = async () => {
        try {
            if (!formikRef?.current) {
                return
            }

            setBulkUnfreezeLoading(true)

            const rowsToUnfreeze = selectedRows.filter(
                row => `${row?.invoice_state ?? ""}`.toLowerCase() === "freeze" ||
                    `${row?.invoice_state ?? ""}`.toLowerCase() === "freezed"
            )

            if (!rowsToUnfreeze.length) {
                enqueueSnackbar("No freezed records selected to unfreeze.", {
                    variant: 'warning',
                    autoHideDuration: 5000,
                });

                return
            }

            const { values } = formikRef.current

            // familyIds comes from the Family filter: a specific family -> [family_id],
            // "all families" (no family selected) -> null.
            const familyIds = values?.family?.family_id
                ? [values.family.family_id]
                : null

            const billingEntityIds = rowsToUnfreeze
                .map(row => row?.billing_entity_id)
                .filter(Boolean)

            const payload = {
                year: Number(values?.year),
                quarter: values?.quarter,
                familyIds,
                loggedinUser: `${iwappRmId}`,
                rmIds: values?.rm?.iwappRmId ? [values.rm.iwappRmId] : null,
                invoiceState: `${values?.status ?? ""}`.toLowerCase() !== 'all' ? values?.status : null,
                billingEntityIds: billingEntityIds.length ? billingEntityIds : null
            }

            const response = await (dispatch(bulkUnfreezeInvoice(payload)) as unknown as Promise<BulkUnfreezeInvoiceResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });

                await fetchReportGenerationTableData({
                    year: values.year,
                    quarter: values.quarter,
                    rm: values.rm,
                    family: values.family,
                    status: values.status
                })

                await handleRefresh()
            } else {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            }
        } catch (error) {
            console.log(error)
        } finally {
            setBulkUnfreezeLoading(false)
        }
    }

    // Bulk generate report: generates reports for all selected rows. Sends the
    // selected billing_entity_ids plus the family/RM filter context. investorType
    // is only meaningful for a single selected row; for multi/all selections it is
    // left blank. Reuses the isBulkDownloadLoading flag (shared "Generate Report"
    // button spinner).
    const handleBulkGenerateReport = async () => {


        try {
            if (!formikRef?.current) {
                return
            }

            if (!selectedRows.length) {
                enqueueSnackbar("Please select at least one member to download the report.", {
                    variant: 'warning',
                    autoHideDuration: 5000,
                });

                return
            }

            setBulkDownloadLoading(true)

            const { values } = formikRef.current

            // familyIds and rmIds come from the filters; billingEntityIds from the selected rows.
            const familyIds = values?.family?.family_id
                ? [`${values.family.family_id}`]
                : null

            const rmIds = values?.rm?.iwappRmId
                ? [`${values.rm.iwappRmId}`]
                : null

            const selectedBillingEntityIds = selectedRows
                .map(row => row?.billing_entity_id)
                .filter(Boolean)
                .map((id) => `${id}`)
            const billingEntityIds = selectedBillingEntityIds.length ? selectedBillingEntityIds : null

            // Entity-level (single row): send that row's type. Multiple or all selected: send "".
            const currentRow = selectedRows.length === 1 ? selectedRows[0] : null
            const investorType = currentRow
                ? `${currentRow?.type ?? ""}`.toLowerCase()
                : ""

            // Entity-level (single row): send that row's own family head. Multiple or
            // all selected: send "" (no single family head applies).
            const familyHead = currentRow
                ? (currentRow?.family_head ?? "")
                : ""
            // invoiceState: "all" status filter -> null. Otherwise a single selected
            // entity sends its own invoice_state; multi/all selections fall back to the
            // applied status filter.
            const invoiceState = `${values?.status ?? ""}`.toLowerCase() !== 'all' ? (currentRow?.invoice_state ?? values?.status)
                : null

            const payload = {
                familyIds,
                year: Number(values?.year),
                quarter: values?.quarter,
                billingEntityIds,
                rmIds,
                investorType,
                familyHead,
                loggedinUser: `${iwappRmId}`,
                invoiceState,
            }

            await handleGenerateReport(payload)
        } catch (error) {
            console.log(error)
        } finally {
            setBulkDownloadLoading(false)
        }
    }

    // Bulk download invoice: requests a combined invoice download for all
    // selected rows, scoped by the family/RM filters and applied status.
    const handleBulkDownloadInvoice = async () => {
        try {
            if (!formikRef?.current) {
                return
            }

            if (!selectedRows.length) {
                enqueueSnackbar("Please select at least one member to download the invoice.", {
                    variant: 'warning',
                    autoHideDuration: 5000,
                });

                return
            }

            setBulkDownloadInvoiceLoading(true)

            const { values } = formikRef.current

            // familyIds comes from the Family filter: a specific family -> [family_id],
            // "all families" (no family selected) -> null.
            const familyIds = values?.family?.family_id
                ? [values.family.family_id]
                : null

            const billingEntityIds = selectedRows
                .map(row => row?.billing_entity_id)
                .filter(Boolean)

            const payload = {
                year: Number(values?.year),
                quarter: values?.quarter,
                familyIds,
                rmIds: values?.rm?.iwappRmId ? [values.rm.iwappRmId] : null,
                invoiceState: `${values?.status ?? ""}`.toLowerCase() !== 'all' ? values?.status : null,
                billingEntityIds: billingEntityIds.length ? billingEntityIds : null
            }

            const response = await (dispatch(bulkDownloadInvoice(payload)) as unknown as Promise<BulkDownloadInvoiceResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });
            } else {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            }
        } catch (error) {
            console.log(error)
        } finally {
            setBulkDownloadInvoiceLoading(false)
        }
    }

    // Bulk share invoice: emails/shares invoices for all selected rows, scoped by
    // the family/RM filters and applied status.
    const handleBulkShareInvoice = async () => {
        try {
            if (!formikRef?.current) {
                return
            }


            if (!selectedRows.length) {
                enqueueSnackbar("Please select at least one member to share the invoice.", {
                    variant: 'warning',
                    autoHideDuration: 5000,
                });

                return
            }

            setBulkShareInvoiceLoading(true)

            const { values } = formikRef.current

            // familyIds comes from the Family filter: a specific family -> [family_id],
            // "all families" (no family selected) -> null.
            const familyIds = values?.family?.family_id
                ? [values.family.family_id]
                : null

            const billingEntityIds = selectedRows
                .map(row => row?.billing_entity_id)
                .filter(Boolean)

            const payload = {
                year: Number(values?.year),
                quarter: values?.quarter,
                familyIds,
                rmIds: values?.rm?.iwappRmId ? [values.rm.iwappRmId] : null,
                invoiceState: `${values?.status ?? ""}`.toLowerCase() !== 'all' ? values?.status : null,
                billingEntityIds: billingEntityIds.length ? billingEntityIds : null
            }

            const response = await (dispatch(bulkShareInvoice(payload)) as unknown as Promise<BulkShareInvoiceResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });
            } else {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            }
        } catch (error) {
            console.log(error)
        } finally {
            setBulkShareInvoiceLoading(false)
        }
    }

    return (
        <>
            <Backdrop
                sx={{
                    color: theme.palette.common.white,
                    zIndex: theme.zIndex.drawer + 1
                }}
                open={isSelectingOptionLoading}
            >
                <CircularProgress sx={{ color: theme.palette.primary[500] }} />
            </Backdrop>
            <Grid container spacing={2} sx={{ marginTop: "25px" }}>
                <Grid item xs={12}>
                    <Box
                        sx={{
                            padding: '25px',
                            borderRadius: '15px',
                            background: theme.palette.common.white,
                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                        }}
                    >
                        <Formik
                            innerRef={formikRef}
                            validationSchema={validationSchema}
                            initialValues={initialValues}
                            onSubmit={handleApplyFilters}
                        >
                            {({ values }) => (
                                <Form>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", md: "row" },
                                            justifyContent: { xs: "flex-start", md: "space-between" },
                                            alignItems: { xs: "flex-start", md: "center" },
                                            rowGap: "20px"
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                rowGap: "10px"
                                            }}
                                        >
                                            <Typography variant='h2'>Report Generation</Typography>
                                            <Typography variant='subtitle_600' sx={{ color: theme.palette.grey[500] }}>
                                                Apply filters below to generate detailed family and member reports
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                columnGap: "10px"
                                            }}
                                        >
                                            <Button
                                                onClick={handleResetFilters}
                                                sx={{
                                                    ...theme.typography.body_500,
                                                    fontWeight: 600,
                                                    color: theme.palette.primary[500],
                                                    padding: '8px'
                                                }}
                                                disabled={isFilterAppliedLoading || isReportGenerationTableDataLoading}
                                            >
                                                Reset Filters
                                            </Button>

                                            <LoadingButton
                                                type="submit"
                                                loading={isFilterAppliedLoading}
                                                disabled={isReportGenerationTableDataLoading}
                                                loadingPosition="center"
                                                variant="outlined"
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    backgroundColor: theme.palette.primary[500],
                                                    padding: "0px 20px",
                                                    alignSelf: "stretch",
                                                    "&: hover": {
                                                        backgroundColor: theme.palette.primary[500],
                                                    },
                                                    "& .MuiCircularProgress-root": {
                                                        color: theme.palette.customColors.aumBlue,
                                                    },
                                                    ...theme.typography.body_500,
                                                    fontWeight: 600
                                                }}
                                            >
                                                Apply Filters
                                            </LoadingButton>
                                        </Box>
                                    </Box>

                                    <Grid
                                        container
                                        spacing={2}
                                        sx={{
                                            alignItems: "stretch",
                                            marginTop: "15px"
                                        }}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            lg={2.4}
                                        >
                                            <MFSelectField
                                                name='year'
                                                label="Financial Year *"
                                                items={YEARS}
                                                disabled={isFilterAppliedLoading || isReportGenerationTableDataLoading}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            lg={2.4}
                                        >
                                            <MFSelectField
                                                name='quarter'
                                                label="Quarter *"
                                                items={QUARTERS}
                                                disabled={isFilterAppliedLoading || isReportGenerationTableDataLoading}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            lg={2.4}
                                        >
                                            <FormikSearchDropdown
                                                name="rm"
                                                label="RM *"
                                                options={rmList ?? []}
                                                fetchData={fetchRmListData}
                                                onChange={handleRmChange}
                                                filterOptions={(x) => x}
                                                onOpen={() => {
                                                    if (!formikRef?.current?.values?.rm) {
                                                        setRmList([]);
                                                    }
                                                }}
                                                disabled={isFilterAppliedLoading || isReportGenerationTableDataLoading}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            lg={2.4}
                                        >
                                            <FormikSearchDropdown
                                                name="family"
                                                label="Family *"
                                                options={familiesList ?? []}
                                                fetchData={fetchFamiliesData}
                                                filterOptions={(x) => x}
                                                onOpen={() => {
                                                    if (!formikRef?.current?.values?.family) {
                                                        setFamiliesList([]);
                                                    }
                                                }}
                                                disabled={!values.rm || isFilterAppliedLoading || isReportGenerationTableDataLoading}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            lg={2.4}
                                        >
                                            <MFSelectField
                                                name='status'
                                                label="Status *"
                                                items={STATUS}
                                                disabled={isFilterAppliedLoading || isReportGenerationTableDataLoading}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Report Generation Table Start */}

                                    <Box sx={{ marginTop: "20px" }}>

                                        {!hasAppliedFilters && <NoData />}

                                        {hasAppliedFilters && (
                                            <>
                                                <ReportGenerationTable
                                                    isLoading={isReportGenerationTableDataLoading}
                                                    tableData={currentTableData}
                                                    tablePaginationObj={reportGenerationTablePaginationObj}
                                                    handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                                                    handleTablePageChange={handleTablePageChange}
                                                    handleMenuItemClick={handleReportGenerationTableMenuItemClick}
                                                    handleCheckboxClick={handleReportGenerationTableCheckboxClick}
                                                    handleSelectAllClick={handleReportGenerationTableSelectAllClick}
                                                    isAllSelected={isAllRecordsSelected}
                                                    isSomeSelected={isSomeRecordsSelected}
                                                />
                                                <Box sx={{
                                                    marginTop: "10px",
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    columnGap: "10px",
                                                    flexWrap: "wrap",
                                                    rowGap: "10px",
                                                }}>
                                                    <LoadingButton
                                                        loading={isBulkFreezeLoading}
                                                        disabled={
                                                            isBulkActionDisabledBase ||
                                                            isStatusFreezed ||
                                                            isStatusNotGenerated
                                                        }
                                                        loadingPosition="center"
                                                        variant="outlined"
                                                        sx={{
                                                            color: theme.palette.text.primary,
                                                            backgroundColor: theme.palette.primary[500],
                                                            padding: "5px 35px",
                                                            "&: hover": {
                                                                backgroundColor: theme.palette.primary[500],
                                                            },
                                                            "& .MuiCircularProgress-root": {
                                                                color: theme.palette.customColors.aumBlue,
                                                            },
                                                            "&.Mui-disabled": {
                                                                pointerEvents: "auto",
                                                                cursor: "not-allowed",
                                                            },
                                                            "&.Mui-disabled:hover": {
                                                                backgroundColor: theme.palette.grey[900],
                                                            },
                                                            ...theme.typography.body_600,
                                                        }}
                                                        onClick={handleBulkFreezeReport}
                                                    >
                                                        Freeze
                                                    </LoadingButton>
                                                    <LoadingButton
                                                        loading={isBulkUnfreezeLoading}
                                                        disabled={
                                                            isBulkActionDisabledBase ||
                                                            isStatusUnfreezed ||
                                                            isStatusNotGenerated}
                                                        loadingPosition="center"
                                                        variant="outlined"
                                                        sx={{
                                                            color: theme.palette.text.primary,
                                                            backgroundColor: theme.palette.primary[500],
                                                            padding: "5px 35px",
                                                            "&: hover": {
                                                                backgroundColor: theme.palette.primary[500],
                                                            },
                                                            "& .MuiCircularProgress-root": {
                                                                color: theme.palette.customColors.aumBlue,
                                                            },
                                                            "&.Mui-disabled": {
                                                                pointerEvents: "auto",
                                                                cursor: "not-allowed",
                                                            },
                                                            "&.Mui-disabled:hover": {
                                                                backgroundColor: theme.palette.grey[900],
                                                            },
                                                            ...theme.typography.body_600,
                                                        }}
                                                        onClick={handleBulkUnfreezeReport}
                                                    >
                                                        Unfreeze
                                                    </LoadingButton>
                                                    <LoadingButton
                                                        loading={isBulkShareInvoiceLoading}
                                                        disabled={
                                                            isBulkActionDisabledBase ||
                                                            isStatusUnfreezed ||
                                                            isStatusNotGenerated
                                                        }
                                                        loadingPosition="center"
                                                        variant="outlined"
                                                        sx={{
                                                            color: theme.palette.text.primary,
                                                            backgroundColor: theme.palette.primary[500],
                                                            padding: "5px 35px",
                                                            "&: hover": {
                                                                backgroundColor: theme.palette.primary[500],
                                                            },
                                                            "& .MuiCircularProgress-root": {
                                                                color: theme.palette.customColors.aumBlue,
                                                            },
                                                            "&.Mui-disabled": {
                                                                pointerEvents: "auto",
                                                                cursor: "not-allowed",
                                                            },
                                                            "&.Mui-disabled:hover": {
                                                                backgroundColor: theme.palette.grey[900],
                                                            },
                                                            ...theme.typography.body_600,
                                                        }}
                                                        onClick={handleBulkShareInvoice}
                                                    >
                                                        Share Invoice
                                                    </LoadingButton>
                                                    <LoadingButton
                                                        loading={isBulkDownloadInvoiceLoading}
                                                        disabled={
                                                            isBulkActionDisabledBase ||
                                                            isStatusUnfreezed ||
                                                            isStatusNotGenerated
                                                        }
                                                        loadingPosition="center"
                                                        variant="outlined"
                                                        sx={{
                                                            color: theme.palette.text.primary,
                                                            backgroundColor: theme.palette.primary[500],
                                                            padding: "5px 35px",
                                                            "&: hover": {
                                                                backgroundColor: theme.palette.primary[500],
                                                            },
                                                            "& .MuiCircularProgress-root": {
                                                                color: theme.palette.customColors.aumBlue,
                                                            },
                                                            "&.Mui-disabled": {
                                                                pointerEvents: "auto",
                                                                cursor: "not-allowed",
                                                            },
                                                            "&.Mui-disabled:hover": {
                                                                backgroundColor: theme.palette.grey[900],
                                                            },
                                                            ...theme.typography.body_600,
                                                        }}
                                                        onClick={handleBulkDownloadInvoice}
                                                    >
                                                        Download Invoice
                                                    </LoadingButton>
                                                    <LoadingButton
                                                        loading={isBulkDownloadLoading}
                                                        disabled={isBulkGenerateReportDisabled}
                                                        loadingPosition="center"
                                                        variant="outlined"
                                                        sx={{
                                                            color: theme.palette.text.primary,
                                                            backgroundColor: theme.palette.primary[500],
                                                            padding: "5px 35px",
                                                            "&: hover": {
                                                                backgroundColor: theme.palette.primary[500],
                                                            },
                                                            "& .MuiCircularProgress-root": {
                                                                color: theme.palette.customColors.aumBlue,
                                                            },
                                                            "&.Mui-disabled": {
                                                                pointerEvents: "auto",
                                                                cursor: "not-allowed",
                                                            },
                                                            "&.Mui-disabled:hover": {
                                                                backgroundColor: theme.palette.grey[900],
                                                            },
                                                            ...theme.typography.body_600,
                                                        }}
                                                        onClick={handleBulkGenerateReport}
                                                    >
                                                        Generate Report
                                                    </LoadingButton>
                                                </Box>
                                            </>
                                        )}
                                    </Box>

                                    {/* Report Generation Table End */}

                                    <Divider
                                        sx={{
                                            mt: 4,
                                            mb: 4,
                                            borderColor: theme.palette.grey[500]
                                        }}
                                    />

                                    {/* Existing Client Statements Start */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            rowGap: "6px"
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                columnGap: "10px",
                                                alignItems: "center"
                                            }}
                                        >
                                            <Typography variant='h2'>
                                                Existing Reports
                                            </Typography>
                                            <IconButton
                                                sx={{
                                                    padding: "0px",
                                                    cursor: 'pointer',
                                                    "&: disabled": {
                                                        opacity: 0.5
                                                    }
                                                }}
                                                onClick={handleRefresh}
                                                disabled={isClientStmtsTableDataLoading}
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                columnGap: "5px",
                                                marginLeft: '-2px',
                                                mt: 0.5
                                            }}>
                                            <InfoIconOutlined />
                                            <Typography variant='subtitle_600' sx={{ color: theme.palette.grey[500] }}>
                                                Statements will be available for 1 day only.
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ marginTop: 5 }}>
                                        <ClientStmtTable
                                            isLoading={isClientStmtsTableDataLoading}
                                            tableData={clientStmtsTableData}
                                            tableSortingObj={clientStmtsTableSortingObj}
                                            handleTableSort={handleClientStmtsTableSort}
                                            tablePaginationObj={clientStmtsTablePaginationObj}
                                            handleTableRowsPerPageChange={handleClientStmtsTableRowsPerPageChange}
                                            handleTablePageChange={handleClientStmtsTablePageChange}
                                            handleMenuItemClick={handleClientStmtTableMenuItemClick}
                                        />
                                    </Box>

                                    {/* Existing Client Statement End */}

                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Grid >
            </Grid >
        </>
    )
}